/**
 * DepositConfig - Secondary configuration section for deposit settings on estimates
 * 
 * Features:
 * - Enable/disable deposit toggle
 * - Type selection (percent or fixed)
 * - Value input with validation
 * - Real-time deposit amount preview
 * 
 * UX Rules:
 * - Calm, uncluttered - this is secondary configuration
 * - Follows existing modal/form design patterns
 * - Shows deposit preview based on estimate total
 */

import { useState, useEffect, useMemo } from "react";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Percent, CurrencyDollar } from "phosphor-react";

const DEPOSIT_TYPES = {
  PERCENT: "PERCENT",
  FIXED: "FIXED",
};

/**
 * Calculate deposit amount for preview
 */
function calculateDepositAmount(total, depositType, depositValue) {
  if (!depositValue || depositValue <= 0) return 0;
  
  if (depositType === DEPOSIT_TYPES.PERCENT) {
    return (total * depositValue) / 100;
  } else if (depositType === DEPOSIT_TYPES.FIXED) {
    return Math.min(depositValue, total); // Can't exceed total
  }
  return 0;
}

export default function DepositConfig({
  depositEnabled,
  depositType,
  depositValue,
  onChange,
  estimateTotal = 0,
  testIdPrefix = "deposit",
}) {
  // Local state for value input (allows empty string for better UX)
  const [localValue, setLocalValue] = useState(
    depositValue != null ? String(depositValue) : ""
  );

  // Sync local value when prop changes (e.g., prefill from defaults)
  useEffect(() => {
    if (depositValue != null) {
      setLocalValue(String(depositValue));
    }
  }, [depositValue]);

  // Calculate preview amount
  const depositAmount = useMemo(() => {
    const numValue = parseFloat(localValue) || 0;
    return calculateDepositAmount(estimateTotal, depositType, numValue);
  }, [estimateTotal, depositType, localValue]);

  // Handle enable/disable toggle
  const handleEnabledChange = (checked) => {
    onChange({
      deposit_enabled: checked,
      deposit_type: depositType || DEPOSIT_TYPES.PERCENT,
      deposit_value: parseFloat(localValue) || null,
    });
  };

  // Handle type change
  const handleTypeChange = (newType) => {
    onChange({
      deposit_enabled: depositEnabled,
      deposit_type: newType,
      deposit_value: parseFloat(localValue) || null,
    });
  };

  // Handle value change
  const handleValueChange = (e) => {
    const raw = e.target.value;
    // Allow empty, digits, and single decimal
    if (raw === "" || /^\d*\.?\d*$/.test(raw)) {
      setLocalValue(raw);
      const numValue = parseFloat(raw);
      if (!isNaN(numValue)) {
        onChange({
          deposit_enabled: depositEnabled,
          deposit_type: depositType,
          deposit_value: numValue,
        });
      }
    }
  };

  // Format value on blur
  const handleValueBlur = () => {
    const numValue = parseFloat(localValue);
    if (!isNaN(numValue) && numValue > 0) {
      // For percent, cap at 100
      const capped = depositType === DEPOSIT_TYPES.PERCENT 
        ? Math.min(numValue, 100) 
        : numValue;
      setLocalValue(capped.toString());
      onChange({
        deposit_enabled: depositEnabled,
        deposit_type: depositType,
        deposit_value: capped,
      });
    } else if (localValue === "" || parseFloat(localValue) === 0) {
      setLocalValue("");
    }
  };

  // Validation state
  const valueError = useMemo(() => {
    if (!depositEnabled) return null;
    const numValue = parseFloat(localValue);
    if (isNaN(numValue) || numValue <= 0) {
      return "Enter a value greater than 0";
    }
    if (depositType === DEPOSIT_TYPES.PERCENT && numValue > 100) {
      return "Percent cannot exceed 100";
    }
    return null;
  }, [depositEnabled, depositType, localValue]);

  return (
    <div 
      className="border border-zinc-200 rounded-lg p-4 bg-zinc-50/50"
      data-testid={`${testIdPrefix}-config-section`}
    >
      {/* Header with toggle */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CurrencyDollar size={18} className="text-zinc-500" />
          <Label className="text-sm font-medium text-zinc-700">
            Require Deposit
          </Label>
        </div>
        <Switch
          checked={depositEnabled}
          onCheckedChange={handleEnabledChange}
          data-testid={`${testIdPrefix}-enabled-toggle`}
        />
      </div>

      {/* Configuration fields (shown when enabled) */}
      {depositEnabled && (
        <div className="space-y-3 pt-2 border-t border-zinc-200">
          {/* Type and Value row */}
          <div className="grid grid-cols-2 gap-3">
            {/* Type selector */}
            <div>
              <Label className="text-xs text-zinc-500 mb-1 block">Type</Label>
              <Select
                value={depositType || DEPOSIT_TYPES.PERCENT}
                onValueChange={handleTypeChange}
              >
                <SelectTrigger 
                  className="h-10 border-2 bg-white"
                  data-testid={`${testIdPrefix}-type-select`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={DEPOSIT_TYPES.PERCENT}>
                    <div className="flex items-center gap-2">
                      <Percent size={14} />
                      <span>Percent</span>
                    </div>
                  </SelectItem>
                  <SelectItem value={DEPOSIT_TYPES.FIXED}>
                    <div className="flex items-center gap-2">
                      <CurrencyDollar size={14} />
                      <span>Fixed Amount</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Value input */}
            <div>
              <Label className="text-xs text-zinc-500 mb-1 block">
                {depositType === DEPOSIT_TYPES.PERCENT ? "Percent" : "Amount"}
              </Label>
              <div className="relative">
                {depositType === DEPOSIT_TYPES.FIXED && (
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">
                    $
                  </span>
                )}
                <Input
                  type="text"
                  inputMode="decimal"
                  value={localValue}
                  onChange={handleValueChange}
                  onBlur={handleValueBlur}
                  placeholder={depositType === DEPOSIT_TYPES.PERCENT ? "30" : "100"}
                  className={`h-10 border-2 bg-white ${
                    depositType === DEPOSIT_TYPES.FIXED ? "pl-7" : ""
                  } ${valueError ? "border-red-300" : ""}`}
                  data-testid={`${testIdPrefix}-value-input`}
                />
                {depositType === DEPOSIT_TYPES.PERCENT && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400">
                    %
                  </span>
                )}
              </div>
              {valueError && (
                <p className="text-xs text-red-500 mt-1">{valueError}</p>
              )}
            </div>
          </div>

          {/* Preview */}
          {estimateTotal > 0 && !valueError && depositAmount > 0 && (
            <div 
              className="flex items-center justify-between bg-emerald-50 rounded-md px-3 py-2 text-sm"
              data-testid={`${testIdPrefix}-preview`}
            >
              <span className="text-emerald-700">Deposit due on approval:</span>
              <span className="font-semibold text-emerald-800">
                ${depositAmount.toFixed(2)}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
